import { User as UserIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ErrorAlert from './ErrorAlert';
import TransitionLink from './TransitionLink';
import { useTransitionNavigate } from '../hooks/useTransitionNavigate';
import { getApiErrorMessage } from '../lib/api-error';
import { isOverdueDue, selectTodayTasks } from '../lib/today-tasks';
import { useCompleteTaskMutation, useGetTasksQuery } from '../store/coachApi';
import type { Task } from '@coachapp/shared';

const TodayTasks = ({ selectedDate }: { selectedDate: string }) => {
  const { t, i18n } = useTranslation();
  const navigate = useTransitionNavigate();
  const { data: tasks = [], error, isLoading } = useGetTasksQuery({ status: 'OPEN' });
  const [completeTask] = useCompleteTaskMutation();
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  if (error || isLoading) return null;

  const now = new Date();
  const visible = selectTodayTasks(tasks, selectedDate, now);
  if (visible.length === 0) return null;

  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';

  const formatDue = (dueAt: string) =>
    new Date(dueAt).toLocaleString(locale, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });

  const handleComplete = async (task: Task) => {
    setCompletingId(task.id);
    setActionError(null);
    try {
      await completeTask(task.id).unwrap();
    } catch (err) {
      setActionError(getApiErrorMessage(err, 'Ошибка при сохранении'));
    } finally {
      setCompletingId(null);
    }
  };

  return (
    <section className="space-y-3 mt-8">
      <h2 className="section-label ml-2">{t('Задачи')}</h2>
      {actionError && <ErrorAlert message={actionError} onDismiss={() => setActionError(null)} />}
      <ul className="space-y-3">
        {visible.map((task) => (
          <li key={task.id} className="card p-4 flex items-start gap-3">
            <button
              type="button"
              onClick={() => void handleComplete(task)}
              disabled={completingId === task.id}
              className="mt-0.5 w-6 h-6 rounded-lg border-2 border-slate-300 hover:border-primary flex items-center justify-center shrink-0 disabled:opacity-50"
              aria-label={t('Отметить выполненной')}
            >
              {completingId === task.id && (
                <div className="w-3 h-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/tasks/${task.id}/edit`, { state: { task } })}
              className="flex-1 min-w-0 text-left"
            >
              <p className="font-semibold text-slate-900">{task.title}</p>
              <p className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                {task.client?.name && (
                  <span className="inline-flex items-center gap-1">
                    <UserIcon size={12} />
                    {task.client.name}
                  </span>
                )}
                {task.dueAt && (
                  <span className={isOverdueDue(task.dueAt, now) ? 'font-semibold text-red-600' : undefined}>
                    {formatDue(task.dueAt)}
                  </span>
                )}
              </p>
            </button>
          </li>
        ))}
      </ul>
      <TransitionLink to="/tasks" className="block text-center text-sm font-semibold text-primary py-2">
        {t('Все задачи')}
      </TransitionLink>
    </section>
  );
};

export default TodayTasks;
